import { validateSheet1HtzService } from './services/validateSheet1HtzService';
import { MakeOrBuyInputType } from './businessLogic';
import { uploadValidator } from './validator';
import uploadConfig from './fileUploadMapping.json';
import {
  convertDropdownLabelsToValues,
  convertGmtDatesToUTCStrings,
  convertNumbers,
  convertToDatabaseColumns,
  getIdFromEsaph,
  isFastTrackMoB,
} from './fileUpload';
import { makeOrBuyRules as fileUploadRules } from './fileUploadBusinessLogic';
import { t } from 'i18next';
import { postRequest } from '../../shared/utils';
import { commitNpiBoardChanges, fetchDataForCommit } from './commitActions';
import {
  calcBusinessDecisions,
  calcFinRepoCommit,
  calcMutationPayload,
  calcNpiCommit,
  calcSaphCommit,
  calcSheet1Commit,
} from './commitCalculations';

export async function CommitHandler(props) {
  const {
    allAccessibleColumns,
    addedRow,
    deletedRow,
    editedRow,
    editedRowOriginal,
    tableDetails,
    userDetails,
  } = props;

  const screenGridId = tableDetails.id;
  const upserts = [...addedRow, ...editedRow];
  const userId = userDetails.userId;

  // Fetch actions
  const {
    poKubuMapping = [],
    saphIds = {},
    sheet1Htzs = {},
  } = await fetchDataForCommit({
    screenGridId,
    upsertedRows: upserts,
  });

  // Calculations
  const decisions = calcBusinessDecisions({
    editedRowOriginal,
    poKubuMapping,
    sheet1Htzs,
    upsertedRecords: upserts,
  });

  const mutationTables = getMutationTableList(allAccessibleColumns);
  const upsertsTableSplit = rowHandler(mutationTables, upserts);

  const npiDeleteIds = deletedRow.map((row) => row.npi_id);
  const { npiInsertObjects, npiUpdateObjects } = calcNpiCommit({
    editedRowOriginal,
    upsertedRecords: upsertsTableSplit,
    userId,
  });

  const { allTableSuccess, saphInsertObjects, saphUpdateObjects } = calcSaphCommit({
    decisions,
    saphIds,
    upsertedRecords: upsertsTableSplit,
    userId,
  });

  const sheet1UpdateObjects = calcSheet1Commit({
    decisions,
    sheet1Htzs,
    upsertedRecords: upsertsTableSplit,
    userId,
  });
  const finRepoUpdateObjects = calcFinRepoCommit({ upsertedRecords: upsertsTableSplit, userId });

  const schemaName = tableDetails.mbdDataGrid.schemaName;
  const { tables, finRepoTable } = calcMutationPayload({
    npiDeleteIds,
    npiInsertObjects,
    npiUpdateObjects,
    saphInsertObjects,
    saphUpdateObjects,
    sheet1UpdateObjects,
    finRepoUpdateObjects,
    schemaName,
  });
  // Mutation actions
  const result = await commitNpiBoardChanges({
    finRepoTable,
    htz: upsertsTableSplit.length ? upsertsTableSplit[0].rowData.npi_htz : '',
    screenGridId,
    tables,
  });
  result.allTableSuccess = allTableSuccess;

  return result;
}

export async function uploadCommitHandler(props) {
  const { allAccessibleColumns, uploadedRows, allDropdowns, tableDetails, userDetails } = props;

  return await validateAndCommit({
    allAccessibleColumns,
    uploadedRows,
    allDropdowns,
    tableDetails,
    userDetails,
  });
}

const validateAndCommit = async ({
  allAccessibleColumns,
  uploadedRows,
  allDropdowns,
  tableDetails,
  userDetails,
}) => {
  const resultSet = {
    data: [],
    errors: [],
  };
  const schemaName = tableDetails.mbdDataGrid.schemaName;
  const screenGridId = tableDetails.id;
  for (const [index, record] of uploadedRows.entries()) {
    // 1. Check for mandatory fields
    const validation = uploadValidator({
      row: record,
      mandatoryColumns: isFastTrackMoB({ mob: record.MOB, labelValueMapping: allDropdowns.npi_mob })
        ? uploadConfig.mandatoryFastTrack
        : uploadConfig.mandatory,
    });
    if (!validation.isValid) {
      resultSet.errors.push({
        rowNo: index,
        htz: record['NPI_HTZ'],
        message: validation.message,
      });
      continue;
    }
    // 2. Convert xlsx column names to DB column names
    const rowWithDbColumnKeys = convertToDatabaseColumns({
      xlsxRow: record,
      columnMapping: uploadConfig.npi,
    });
    const { npi_htz: npiHtz } = rowWithDbColumnKeys as { npi_htz: string };
    // 3. Do e_saph check
    const eSaphId = await getIdFromEsaph({ npiHtz, schemaName, screenGridId });
    if (eSaphId !== null) {
      resultSet.errors.push({
        htz: npiHtz,
        message: t('mob.air_supply_cannot_updated', { npiHtz }),
        rowNo: index,
        isError: true,
      });
      continue;
    }
    // 4. Convert drop down values to ids
    const tempRow = convertDropdownLabelsToValues({
      row: rowWithDbColumnKeys,
      dropdownMapping: allDropdowns,
    });
    // 5. Convert string column data to strings and number column data to numbers
    const tempRow2 = convertNumbers({ row: tempRow, allAccessibleColumns });
    // 6. Convert date values to strings, otherwise date stored in DB is one day off
    const rowToCommit = {
      ...convertGmtDatesToUTCStrings({ row: tempRow2 }),
      created_by: userDetails.userId,
      updated_by: userDetails.userId,
      update_date: new Date().toISOString().substring(0, 19),
    };
    // 7. Store data in DB
    const result = await doCommit({
      record: rowToCommit,
      tableDetails,
    });
    if (result?.isError) {
      resultSet.errors.push({ ...result, rowNo: index, isError: true });
    } else resultSet.data.push({ ...result, rowNo: index });
  }
  return resultSet;
};

function getMutationTableList(allAccessibleColumns) {
  const list = allAccessibleColumns.reduce((resp, item) => {
    const { mutationTableName } = item;
    if (!mutationTableName) {
      return resp;
    }
    if (!resp?.[mutationTableName]) {
      resp[mutationTableName] = [];
    }
    resp[mutationTableName].push(item);
    return resp;
  }, {});
  return list;
}

const rowHandler = (mutationTables, rowDetails) => {
  const reqData = rowDetails.reduce((data, item) => {
    const groupRecord = {};
    groupRecord['rowData'] = item;
    for (const key in mutationTables) {
      groupRecord[key] = mutationTables[key].reduce((resp, record) => {
        const { mutationColumnName, attrInternalName: colInternalName } = record;
        resp[mutationColumnName ?? colInternalName] = item[colInternalName];
        return resp;
      }, {});
    }
    data.push(groupRecord);
    return data;
  }, []);

  return reqData;
};

type UploadResultType = {
  htz: string;
  message: string;
  isError?: boolean;
};

async function doCommit({ record, tableDetails }): Promise<UploadResultType> {
  const {
    bestellnummer: bestellNummer,
    debitor_name: debitorName,
    design_owner_id: designOwnerId,
    htz_type_id: htzTypeId,
    khk_pkey: khkPkey,
    lumpsum_due: lumpSumDue,
    npi_htz: npiHtz,
    npi_mob: makeOrBuy,
    novelty_id: noveltyId,
    npi_accept_po: npiAcceptPO,
    npi_as_kenner: npiAsKenner,
    npi_comment: npiComment,
    nrp_due: nrpDue,
    pag_deliverydate_due: pagDeliveryDateDue,
    pag_followup_date: pagFollowUpDate,
    pag_ispartof: pagIsPartOf,
    pre_htz: preHtz,
    pre_htz_manufactured_by: preHtzManufacturedBy,
    project_id: projectId,
    rp_due: rpDue,
  } = record;
  try {
    const schemaName = tableDetails.mbdDataGrid.schemaName;
    const screenGridId = tableDetails.id;
    // Figure out if in npi table we need to update existing record or insert new record
    const body = {
      columns: ['npi_id', 'npi_mob'],
      screenGridId,
      schema: schemaName,
      table: 'npi',
      key: 'npi_htz',
      value: npiHtz,
    };
    const { data, errors } = await postRequest('/api/records', body);
    if (errors) {
      console.error(errors);
      throw new Error(errors[0]?.message);
    }

    const npiId = data?.[`${schemaName}_npi`]?.length ? data[`${schemaName}_npi`][0].npi_id : null;
    const makeOrBuyInDB = data?.[`${schemaName}_npi`]?.length
      ? data[`${schemaName}_npi`][0].npi_mob
      : null;
    const makeOrBuyModified = (makeOrBuyInDB && makeOrBuyInDB !== makeOrBuy) || false;

    const sheet1Htz = await validateSheet1HtzService(npiHtz, screenGridId);

    const decisionTreeInput: MakeOrBuyInputType = {
      bestellNummer,
      debitorName,
      designOwnerId,
      htzTypeId,
      khkPkey,
      lumpSumDue,
      makeOrBuy,
      makeOrBuyModified,
      noveltyId,
      npiAcceptPO,
      npiAsKenner,
      npiComment,
      nrpDue,
      pagDeliveryDateDue,
      pagFollowUpDate,
      pagIsPartOf,
      poKubu: null,
      preHtz,
      preHtzManufacturedBy,
      projectId,
      rpDue,
      sheet1Htz,
    };
    const decisionTreeResult = fileUploadRules(decisionTreeInput);
    if (decisionTreeResult.error) {
      return { htz: record.npi_htz, message: decisionTreeResult.error, isError: true };
    }
    if (!decisionTreeResult.save) {
      return { htz: record.npi_htz, message: t('mob.no_commit') };
    }
    const dataToSave = Object.assign({}, { npi: record, sheet1: {} });
    if (decisionTreeResult.data) {
      updateRecordWithDecisionTreeResult(dataToSave, decisionTreeResult.data);
    }

    const npiObjects = {};
    if (npiId) {
      const where = {};
      where['npi_id'] = {
        _eq: npiId,
      };
      const npiWithUpdatedBy = JSON.parse(JSON.stringify(dataToSave.npi));
      delete npiWithUpdatedBy.created_by;
      npiObjects['updateObjects'] = [
        {
          where,
          _set: npiWithUpdatedBy,
        },
      ];
    } else {
      const npiWithCreatedBy = JSON.parse(JSON.stringify(dataToSave.npi));
      npiObjects['insertObjects'] = [npiWithCreatedBy];
    }
    const npiPayload = {
      ...npiObjects,
      pkey: 'npi_id',
      schema: schemaName,
      table: 'npi',
    };

    const tablesToUpdate = [npiPayload];
    const sheet1Update = decisionTreeResult.data?.find((item) => item.table === 'sheet1');
    if (sheet1Update) {
      const where = {};
      where['htz'] = { _eq: record.npi_htz };
      const sheet1Objects = {};
      sheet1Objects['updateObjects'] = [
        {
          where,
          _set: dataToSave.sheet1,
        },
      ];
      tablesToUpdate.push({
        ...sheet1Objects,
        pkey: 'id1',
        schema: schemaName,
        table: 'sheet1',
      });
    }

    const result = await postRequest('/api/upload', {
      screenGridId,
      tables: tablesToUpdate,
      uploadType: 'upload_htz_list',
    });
    if (result.errors?.length) {
      return {
        htz: record.npi_htz,
        message: t('mob.insert_update_error', {
          htz: record.npi_htz,
          msg:
            result.errors[0].extensions?.internal?.error?.message ||
            result.errors[0].message ||
            'unknown database error',
        }),
        isError: true,
      };
    }

    return {
      htz: record.npi_htz,
      message: t('mob.upload_success', {
        status: npiId ? 'Updated' : 'Created',
        htz: record.npi_htz,
      }),
    };
  } catch (error) {
    console.error(error);
    return {
      htz: record.npi_htz,
      message: t('mob.insert_update_error', {
        htz: record.npi_htz,
        msg: error.message || 'unknown exception',
      }),
      isError: true,
    };
  }
}

function updateRecordWithDecisionTreeResult(record, decisionTreeData) {
  decisionTreeData.forEach((item) => {
    const tableToUpdate = record[item.table];
    for (const [columnName, columnValue] of item.fields) {
      tableToUpdate[columnName] = columnValue;
    }
  });
}
